import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import type { UserStatus } from '@/types/api';

/**
 * Timed self-status (Discord "set status for 1 hour" etc.). The chosen status
 * is applied immediately via api.updateMe; a timed status also persists an
 * expiry to localStorage and schedules a revert to ONLINE. The expiry is
 * re-checked on boot (initStatusTimer) so a status that elapsed while the app
 * was closed is reverted, and one still in the future is re-scheduled.
 */
const KEY = 'bunker.statusUntil.v1';
let timer: ReturnType<typeof setTimeout> | null = null;

function clearStored() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* private mode */
  }
}

function store(until: number) {
  try {
    localStorage.setItem(KEY, String(until));
  } catch {
    /* private mode */
  }
}

async function revertToOnline() {
  clearStored();
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  try {
    const me = await api.updateMe({ status: 'ONLINE' });
    useAuthStore.getState().setUser(me);
  } catch {
    /* best-effort — next boot will retry from the persisted expiry */
  }
}

function schedule(until: number) {
  if (timer) clearTimeout(timer);
  const ms = until - Date.now();
  if (ms <= 0) {
    void revertToOnline();
    return;
  }
  store(until);
  // Max timed status is 3 days, well under setTimeout's ~24.8-day ceiling.
  timer = setTimeout(() => void revertToOnline(), ms);
}

/**
 * Apply a status now. `durationMs = null` is permanent (clears any pending
 * revert); a number schedules a revert to ONLINE after that long.
 * Throws on API failure so the caller can surface a toast.
 */
export async function setStatusFor(status: UserStatus, durationMs: number | null): Promise<void> {
  const me = await api.updateMe({ status });
  useAuthStore.getState().setUser(me);
  if (durationMs == null) {
    clearStored();
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    return;
  }
  schedule(Date.now() + durationMs);
}

/** Re-arm (or fire) a persisted timed status. Call once on app boot. */
export function initStatusTimer(): void {
  let until = 0;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) until = Number(raw) || 0;
  } catch {
    /* private mode */
  }
  if (!until) return;
  if (until <= Date.now()) void revertToOnline();
  else schedule(until);
}
